import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faEdit, faSearch, faUserSecret } from '@fortawesome/free-solid-svg-icons';
import { isSuperAdmin } from '../../utils/permissions';
import { api, getApiUrl } from '../../utils/apiConfig';

function UserManagement({ userId, currentUser, onImpersonateUser, refreshTrigger, onRefresh }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // Track input value separately
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [userStatus, setUserStatus] = useState(false); // false = active, true = deleted
  const [showModal, setShowModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'last_login',
    direction: 'descending'
  });

  // Add refs to track if we're currently fetching data
  const isFetchingUsersRef = useRef(false);
  const isFetchingRolesRef = useRef(false);
  const isFetchingAccountsRef = useRef(false);

  // Add a ref to track the debounce timeout for search
  const searchDebounceRef = useRef(null);

  // Memoize the fetch functions to prevent unnecessary re-creation
  const fetchUsers = useCallback(async () => {
    // If we're already fetching users, don't start another fetch
    if (isFetchingUsersRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingUsersRef.current = true;
      setIsLoading(true);

      const response = await api.get(getApiUrl('/api/admin/users'), {
        params: { userId }
      });
      setUsers(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again later.');
      setIsLoading(false);
    } finally {
      // Reset the fetching flag
      isFetchingUsersRef.current = false;
    }
  }, [userId]);

  const fetchRoles = useCallback(async (accountIdForFilter = null) => {
    // If we're already fetching roles, don't start another fetch
    if (isFetchingRolesRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingRolesRef.current = true;

      const params = { userId };
      // If accountId is provided, filter specialist roles by account's enabled channels
      if (accountIdForFilter) {
        params.accountId = accountIdForFilter;
      }

      const response = await api.get(getApiUrl('/api/admin/roles'), { params });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      // Reset the fetching flag
      isFetchingRolesRef.current = false;
    }
  }, [userId]);

  const fetchAccounts = useCallback(async () => {
    // If we're already fetching accounts, don't start another fetch
    if (isFetchingAccountsRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingAccountsRef.current = true;

      const response = await api.get(getApiUrl('/api/admin/accounts'), {
        params: { userId }
      });
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      // Reset the fetching flag
      isFetchingAccountsRef.current = false;
    }
  }, [userId]);

  // Fetch data when the component mounts or refreshTrigger changes
  useEffect(() => {
    // Use a small timeout to debounce multiple rapid calls
    const debounceTimeout = setTimeout(() => {
      fetchUsers();
      fetchRoles();
      fetchAccounts();
    }, 50);

    // Clean up the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [fetchUsers, fetchRoles, fetchAccounts, refreshTrigger]);

  const handleEditRole = (user) => {
    setEditingUser(user);
    setSelectedRole(user.role_id || '');

    // Get the user's role
    const userRole = roles.find(r => r.id === user.role_id)?.name;

    // Set selected accounts from the user's accounts array
    let accountIdForFilter = null;
    if (user.accounts && user.accounts.length > 0) {
      // If the user is not an Admin and has multiple accounts, only select the first one
      if (userRole !== 'Admin' && user.accounts.length > 1) {
        setSelectedAccounts([user.accounts[0].id]);
        accountIdForFilter = user.accounts[0].id;
      } else {
        setSelectedAccounts(user.accounts.map(account => account.id));
        // For filtering, use the first account
        accountIdForFilter = user.accounts[0].id;
      }
    } else if (user.account_id) {
      // For backward compatibility
      setSelectedAccounts([user.account_id]);
      accountIdForFilter = user.account_id;
    } else {
      setSelectedAccounts([]);
    }

    // Fetch roles filtered by the user's account's enabled channels
    if (accountIdForFilter) {
      fetchRoles(accountIdForFilter);
    } else {
      fetchRoles();
    }

    // Set the user status
    setUserStatus(user.is_deleted || false);

    setShowModal(true);
  };

  const handleSaveRole = async () => {
    // Validate required fields
    if (!selectedRole) {
      setError('Please select a role for the user');
      return;
    }

    if (selectedAccounts.length === 0) {
      setError('Please select at least one account for the user');
      return;
    }

    // Get the selected role object
    const selectedRoleObj = roles.find(r => r.id === selectedRole);

    // Prevent non-Super Admins from assigning Super Admin role
    if (selectedRoleObj?.name === 'Super Admin' && !isSuperAdmin(currentUser)) {
      setError('You do not have permission to assign the Super Admin role');
      return;
    }

    // For non-Admin roles, ensure only one account is selected
    if (selectedRoleObj?.name !== 'Admin' && selectedAccounts.length > 1) {
      setError('Non-Admin users can only be assigned to a single account');
      return;
    }

    try {
      setError(null);
      let roleResponse = { data: { role_id: editingUser.role_id, role: editingUser.role, role_name: editingUser.role_name } };

      // Update role if it's different from the current role_id
      if (selectedRole !== editingUser.role_id) {
        roleResponse = await api.put(getApiUrl(`/api/admin/users/${editingUser.id}/role`), {
          role_id: selectedRole,
          userId // Include userId for authentication
        });
      }

      // Update user status if it's different from the current status
      if (userStatus !== editingUser.is_deleted) {
        await api.put(getApiUrl(`/api/admin/users/${editingUser.id}/status`), {
          is_deleted: userStatus,
          userId // Include userId for authentication
        });
      }

      // Get current user accounts
      const currentAccounts = editingUser.accounts?.map(a => a.id) ||
                             (editingUser.account_id ? [editingUser.account_id] : []);

      // Process account assignments
      const accountResponses = [];

      // Add user to new accounts
      for (const accountId of selectedAccounts) {
        if (!currentAccounts.includes(accountId)) {
          console.log(`Adding user to account: ${accountId}`);

          // Determine if the user should be an account admin
          // If they have the Admin role, they should automatically be an account admin
          const isUserAdmin = roleResponse.data.role_name === 'Admin';

          // Use the account-admin endpoint instead of the admin endpoint
          const response = await api.post(getApiUrl(`/api/account-admin/accounts/${accountId}/users`), {
            userId,
            targetUserId: editingUser.id,
            isAccountAdmin: isUserAdmin // Automatically make Admins account admins
          });
          accountResponses.push(response);

          // If the user is an Admin but not yet an account admin, update their status
          if (isUserAdmin && response.data && !response.data.is_account_admin) {
            // Use the account-admin endpoint instead of the admin endpoint
            await api.put(getApiUrl(`/api/account-admin/accounts/${accountId}/users/${editingUser.id}`), {
              userId,
              isAccountAdmin: true
            });
          }
        }
      }

      // Remove user from accounts that were unselected
      for (const accountId of currentAccounts) {
        if (!selectedAccounts.includes(accountId)) {
          console.log(`Removing user from account: ${accountId}`);
          // Use the account-admin endpoint instead of the admin endpoint
          await api.delete(getApiUrl(`/api/account-admin/accounts/${accountId}/users/${editingUser.id}`), {
            data: { userId }
          });
        }
      }

      setShowModal(false);
      setEditingUser(null);

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user. Please try again.');
    }
  };

  // Create a debounced search function to prevent rapid consecutive renders
  const handleSearch = useCallback((e) => {
    // Update the input value immediately for responsive UI
    setSearchInputValue(e.target.value);

    // Clear any existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set a new timeout for the actual filtering
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(e.target.value);
    }, 300); // Use a longer debounce time for search (300ms)
  }, []);

  // Clean up the search debounce timeout when the component unmounts
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get the sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      // Handle null or undefined values
      if (a[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? 1 : -1;
      if (b[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? -1 : 1;

      // Handle date fields
      if (sortConfig.key === 'created_at' || sortConfig.key === 'last_login') {
        const dateA = a[sortConfig.key] ? new Date(a[sortConfig.key]) : new Date(0);
        const dateB = b[sortConfig.key] ? new Date(b[sortConfig.key]) : new Date(0);
        return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
      }

      // Handle string fields
      if (typeof a[sortConfig.key] === 'string' && typeof b[sortConfig.key] === 'string') {
        return sortConfig.direction === 'ascending'
          ? a[sortConfig.key].localeCompare(b[sortConfig.key])
          : b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }

      // Handle other fields
      return sortConfig.direction === 'ascending'
        ? a[sortConfig.key] > b[sortConfig.key] ? 1 : -1
        : a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });
  };

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;

    const term = searchTerm.toLowerCase();
    const email = (user.email || '').toLowerCase();
    const displayName = (user.display_name || '').toLowerCase();

    return email.includes(term) || displayName.includes(term);
  });

  // Apply sorting to filtered users
  const sortedUsers = getSortedData(filteredUsers);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          {/* <div className="loading-spinner"></div> */}
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchUsers}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <h2 className="admin-card-title">
        <FontAwesomeIcon icon={faUsers} className="icon" />
        User Management
      </h2>

      <div className="search-bar">
        <input
          type="text"
          className="search-input"
          placeholder="Search by email or name..."
          value={searchInputValue}
          onChange={handleSearch}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearchTerm(searchInputValue);
            }
          }}
        />
        <button
          className="search-button"
          onClick={() => setSearchTerm(searchInputValue)}
        >
          <FontAwesomeIcon icon={faSearch} />
        </button>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faUsers} className="empty-state-icon" />
          <p className="empty-state-text">No users found</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('email')} className="sortable-header">
                  Email{getSortDirectionIndicator('email')}
                </th>
                <th onClick={() => requestSort('display_name')} className="sortable-header">
                  Display Name{getSortDirectionIndicator('display_name')}
                </th>
                <th onClick={() => requestSort('role_name')} className="sortable-header">
                  Role{getSortDirectionIndicator('role_name')}
                </th>
                <th onClick={() => requestSort('account_name')} className="sortable-header">
                  Account{getSortDirectionIndicator('account_name')}
                </th>
                <th onClick={() => requestSort('last_login')} className="sortable-header">
                  Last Login{getSortDirectionIndicator('last_login')}
                </th>
                <th onClick={() => requestSort('is_deleted')} className="sortable-header">
                  Status{getSortDirectionIndicator('is_deleted')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Create a map to deduplicate users by ID */}
              {(() => {
                // Create a map to store unique users by ID
                const uniqueUsers = new Map();

                // Process each user to handle duplicates
                sortedUsers.forEach(user => {
                  // If this user ID is already in our map
                  if (uniqueUsers.has(user.id)) {
                    const existingUser = uniqueUsers.get(user.id);

                    // If the current user has an account but the existing one doesn't,
                    // or if we want to update any other properties, do it here
                    if (user.account_name && !existingUser.account_name) {
                      existingUser.account_name = user.account_name;
                      existingUser.account_id = user.account_id;
                    }

                    // If the user belongs to multiple accounts, we could add them to an array
                    if (!existingUser.accounts) {
                      existingUser.accounts = [];
                    }

                    if (user.account_name && !existingUser.accounts.some(a => a.id === user.account_id)) {
                      existingUser.accounts.push({
                        id: user.account_id,
                        name: user.account_name
                      });
                    }
                  } else {
                    // First time seeing this user, initialize accounts array if they have an account
                    if (user.account_name) {
                      user.accounts = [{
                        id: user.account_id,
                        name: user.account_name
                      }];
                    } else {
                      user.accounts = [];
                    }

                    // Add to our map
                    uniqueUsers.set(user.id, user);
                  }
                });

                // Convert the map back to an array for rendering
                return Array.from(uniqueUsers.values()).map(user => (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>{user.display_name || '-'}</td>
                    <td>
                      <span className={`badge badge-${(user.role_name === 'Admin' || user.role_name === 'Super Admin') ? 'primary' : 'success'}`}>
                        {user.role_name || user.role}
                      </span>
                    </td>
                    <td>
                      {user.accounts && user.accounts.length > 0 ? (
                        <div>
                          {user.accounts.map((account) => (
                            <span key={account.id} className="badge badge-info mr-1 mb-1">
                              {account.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{formatDate(user.last_login)}</td>
                    <td>
                      {user.is_deleted ? (
                        <span className="badge badge-secondary">Inactive</span>
                      ) : (
                        <span className="badge badge-success">Active</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {/* Edit button logic:
                            1. Super Admins can edit anyone
                            2. Regular Admins can edit other regular Admins and non-Admin users
                            3. Nobody except Super Admins can edit Super Admins */}
                        {(isSuperAdmin(currentUser) ||
                          user.role_name !== 'Super Admin') && (
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditRole(user)}
                            title="Edit Role"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        )}

                        {/* Show impersonation button only if the logged-in user is a Super Admin and the listed user is not */}
                        {isSuperAdmin(currentUser) && user.role_name !== 'Super Admin' && (
                          <button
                            className="btn btn-sm btn-info ml-1"
                            onClick={() => onImpersonateUser(user.id)}
                            title="Impersonate User"
                          >
                            <FontAwesomeIcon icon={faUserSecret} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Edit User Role & Account Assignments</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p><strong>User:</strong> {editingUser.email}</p>

              {/* Show warning for regular admins editing other admins */}
              {!isSuperAdmin(currentUser) && editingUser.role_name === 'Admin' && (
                <div className="alert alert-info">
                  <small>
                    Note: As a regular Admin, you can edit other Admin users but cannot assign them the Super Admin role.
                  </small>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="role">Role <span className="text-danger">*</span></label>
                <select
                  id="role"
                  value={selectedRole}
                  onChange={(e) => {
                    setSelectedRole(e.target.value);
                    // If changing from Admin to non-Admin role, reset to single account selection
                    const newRole = roles.find(r => r.id === e.target.value);
                    if (newRole && newRole.name !== 'Admin' && selectedAccounts.length > 1) {
                      setSelectedAccounts([selectedAccounts[0]]);
                    }
                  }}
                  className="form-control"
                >
                  <option value="">-- Select a Role --</option>
                  {roles
                    // Filter out Super Admin role for non-Super Admin users
                    .filter(role => isSuperAdmin(currentUser) || role.name !== 'Super Admin')
                    .map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))
                  }
                </select>
              </div>

              <div className="form-group">
                <label>Accounts <span className="text-danger">*</span></label>

                {/* Determine if we should show multiple account selection */}
                {selectedRole && roles.find(r => r.id === selectedRole)?.name === 'Admin' ? (
                  /* Multiple account selection for Admin role */
                  <div className="account-selection">
                    {accounts.map(account => (
                      <div key={account.id} className="account-checkbox">
                        <input
                          type="checkbox"
                          id={`account-${account.id}`}
                          checked={selectedAccounts.includes(account.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAccounts([...selectedAccounts, account.id]);
                            } else {
                              setSelectedAccounts(selectedAccounts.filter(id => id !== account.id));
                            }
                          }}
                        />
                        <label htmlFor={`account-${account.id}`} className="ml-2">
                          {account.name}
                        </label>
                      </div>
                    ))}

                    <p className="text-info mt-2">
                      <small>
                        <strong>Note:</strong> Users with Admin role will automatically be made account admins of their assigned accounts.
                      </small>
                    </p>
                  </div>
                ) : (
                  /* Single account selection dropdown for non-Admin roles */
                  <select
                    className="form-control"
                    value={selectedAccounts.length > 0 ? selectedAccounts[0] : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        setSelectedAccounts([e.target.value]);
                        // Re-fetch roles filtered by this account's enabled channels
                        fetchRoles(e.target.value);
                      } else {
                        setSelectedAccounts([]);
                        // Fetch all roles when no account selected
                        fetchRoles();
                      }
                    }}
                  >
                    <option value="">-- Select an Account --</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                )}

                {accounts.length === 0 && (
                  <p className="text-muted">No accounts available</p>
                )}
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-control"
                  value={userStatus ? "inactive" : "active"}
                  onChange={(e) => setUserStatus(e.target.value === "inactive")}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Account admin field removed */}
            </div>
            <div className="modal-footer">
              <button className="btn btn-success" onClick={handleSaveRole}>Save Changes</button>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
